import { useEffect, useState } from 'react';
import useSaveSimulationCustomerGranteesMutation from './useSaveSimulationCustomerGranteesMutation';
import { formatProjectBeforeSubmit } from '@@viewHelpers/format.jsx';
import { isNilOrEmpty } from '@@serverHelpers/index.mjs';
import {
  assocPath,
  compose,
  concat,
  defaultTo,
  dissoc,
  find,
  findIndex,
  path,
  prop,
  propEq,
  propOr,
  startsWith
} from 'ramda';
import { getZipCodeRules } from '@@viewHelpers/zipCode';
import { GranteeTypeEnum } from '@fasstech/oip-core/entities/Grantee/enums';
import { useProcess } from '@fasstech/oip-starter-utils/app';
import usePavillonSearchCustomerSalesRightsMutation from './usePavillonSearchCustomerSalesRightsMutation.js';
import trackLeadSimulationExtra from '../extra/trackLeadSimulationExtra';
import { useAnalytics } from '@@app/context/analytics-context';
import { formattedRegime } from '../../../_common/common/helpers/formattedRegime.mjs';
import { getCaisseFromValue } from '@@viewCommonComponents/forms/options';

const BECLM_BLOCK_ERROR_PREFIX = 'BECLM_BLACKLIST_';
const BECLM_FAILED_ERROR = 'BECLM_BLACKLIST_FAILED';
const BECLM_MODAL_TEXT_KO = 'PROCESSDEVENTE_RETOUR_BECLM_KO_LABEL'; // FAILED (MATCH / STRUCK_OFF)
const BECLM_MODAL_TEXT_PENDING = 'PROCESSDEVENTE_RETOUR_BECLM_EN_ATTENTE_LABEL'; // BLOCKED (POTENTIAL_RISK / UNKNOWN)
const NO_ELIGIBLE_OFFER_ERROR = 'NO_ELIGIBLE_OFFER';

const useSimulationCustomerGranteesForm = ({
  jobs,
  professionalSituations,
  form,
  getMiddleBoolean,
  middleFeatures,
  getMiddleText,
  initialBeclmBlocked = false
}) => {
  const [loading, setLoading] = useState(false);
  const [unauthorizedModalMessage, setUnauthorizedModalMessage] = useState('');
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const [beclmBlocked, setBeclmBlocked] = useState(initialBeclmBlocked);
  const [noEligibleOffer, setNoEligibleOffer] = useState(false);

  const [beclmModalTextKey, setBeclmModalTextKey] = useState(
    initialBeclmBlocked ? BECLM_MODAL_TEXT_KO : null
  );
  const { processHandlers, context } = useProcess();

  useEffect(() => {
    if (initialBeclmBlocked) {
      console.log('[BECLM] Definitive block - view frozen (SUBSCRIPTION_FAILED)');
    }
  }, [initialBeclmBlocked]);

  const { onFormSubmit, loading: isFetchingForm } = useSaveSimulationCustomerGranteesMutation();

  const { onFormSubmit: onPavillonSearchCustomerSalesRights } =
    usePavillonSearchCustomerSalesRightsMutation();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    setIsUnauthorized(false);
  }, [
    form.watch('customer.firstName'),
    form.watch('customer.lastName'),
    form.watch('customer.birthDate')
  ]);

  useEffect(() => {
    setNoEligibleOffer(false);
  }, [
    form.watch('customer.birthDate'),
    form.watch('customer.addresses.0.zipCode'),
    form.watch('customer.socialSecurities.0.isCSS'),
    form.watch('customer.CSPs.0.categoryCSPExternalId'),
    form.watch('customer.CSPs.0.categoryLvl1CSPExternalId'),
    form.watch('customer.isTNS'),
    form.watch('customer.salesArea.country')
  ]);
  const onSubmit = (values) => {
    if (loading) return;
    trackLeadSimulationExtra(trackEvent, getMiddleText);
    setLoading(true);
    let formattedValues = dissoc('beclmBlocked', formatProjectBeforeSubmit(values, getMiddleText));
    const customerCSPExternalId = path(['customer', 'CSPs', 0, 'externalId'], values);

    if (!isNilOrEmpty(customerCSPExternalId)) {
      const CSPLabel = compose(
        prop('label'),
        defaultTo({}),
        find(propEq('value', customerCSPExternalId))
      )(jobs);

      formattedValues = assocPath(['customer', 'CSPs', 0, 'label'], CSPLabel, formattedValues);
    }

    const spouseIndex = compose(
      findIndex(propEq('type', GranteeTypeEnum.SPOUSE)),
      propOr([], 'grantees')
    )(values);
    const spouseCSPExternalId = path(['grantees', spouseIndex, 'CSPs', 0, 'externalId'], values);

    if (!isNilOrEmpty(spouseCSPExternalId)) {
      const CSPLabel = compose(
        prop('label'),
        defaultTo({}),
        find(propEq('value', spouseCSPExternalId))
      )(jobs);

      formattedValues = assocPath(
        ['grantees', spouseIndex, 'CSPs', 0, 'label'],
        CSPLabel,
        formattedValues
      );
    }

    const customerCategoryLvl1CSPExternalId = path(
      ['customer', 'CSPs', 0, 'categoryLvl1CSPExternalId'],
      values
    );

    if (!isNilOrEmpty(customerCategoryLvl1CSPExternalId)) {
      const CSPCategoryLvl1Label = compose(
        prop('label'),
        defaultTo({}),
        find(propEq('value', customerCategoryLvl1CSPExternalId))
      )(professionalSituations);

      formattedValues = assocPath(
        ['customer', 'CSPs', 0, 'categoryLvl1Label'],
        CSPCategoryLvl1Label,
        formattedValues
      );
    }

    const spouseCategoryLvl1CSPExternalId = path(
      ['grantees', spouseIndex, 'CSPs', 0, 'categoryLvl1CSPExternalId'],
      values
    );

    if (!isNilOrEmpty(spouseCategoryLvl1CSPExternalId)) {
      const CSPCategoryLvl1Label = compose(
        prop('label'),
        defaultTo({}),
        find(propEq('value', spouseCategoryLvl1CSPExternalId))
      )(professionalSituations);

      formattedValues = assocPath(
        ['grantees', spouseIndex, 'CSPs', 0, 'categoryLvl1Label'],
        CSPCategoryLvl1Label,
        formattedValues
      );
    }

    if (
      getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT') &&
      getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_CONTRAT')
    ) {

      const caissePro = getCaisseFromValue(
        path(['customer', 'CSPs', 0, 'externalId'], values),
        jobs
      );

      formattedValues = assocPath(
        ['customer', 'socialSecurities', 0, 'regime'],
        concat('REGIME_PRO_', formattedRegime(caissePro)),
        formattedValues
      );

      formattedValues = assocPath(
        ['customer', 'socialSecurities', 0, 'caissePro'],
        undefined,
        formattedValues
      );
    }

    onPavillonSearchCustomerSalesRights({ customer: formattedValues.customer }, (ok, err, data) => {
      if (ok && !err) {
        setIsUnauthorized(false);
        onFormSubmit(formattedValues, (ok, err) => {
          if (ok && !err) {
            processHandlers.onNext({
              context: {
                projectId: context?.projectId
              }
            });
          } else if (startsWith(BECLM_BLOCK_ERROR_PREFIX, defaultTo('', err))) {
            const isFailed = err === BECLM_FAILED_ERROR;
            console.log(
              isFailed
                ? '[BECLM] Definitive block - view frozen (SUBSCRIPTION_FAILED)'
                : `[BECLM] Flow blocked - view frozen until reload (${err})`
            );
            setBeclmBlocked(true);
            setBeclmModalTextKey(isFailed ? BECLM_MODAL_TEXT_KO : BECLM_MODAL_TEXT_PENDING);
            setLoading(false);
          } else if (err === NO_ELIGIBLE_OFFER_ERROR) {
            setNoEligibleOffer(true);
            setLoading(false);
          } else {
            setNoEligibleOffer(false);
            setLoading(false);
          }
        });
      } else {
        setLoading(false);
        if (err && propEq('code', 'SALES_UNAUTHORIZED', data)) {
          setUnauthorizedModalMessage(err);
          setIsUnauthorized(true);
        }
      }
    });
  };

  const { ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES = [] } = middleFeatures;
  const postalCodeFranceMetro = getZipCodeRules(
    'France Métropolitaine',
    ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES
  );
  const postalCodeDROM = getZipCodeRules('DROM', ADHERENT_CODE_POSTAL_FILTRES_GEOGRAPHIQUES);

  const $onPrevClick = () => {
    processHandlers.onPrev({
      context: {
        projectId: context?.projectId
      }
    });
  };

  const { isValid } = form.formState;

  const displayAdditionalInfo =
    (getMiddleBoolean('ADHERENT_REGIME_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_REGIME_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_TEL_FIXE_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_NSS_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_NSS_EST_DEMANDE_DONNEES_CONTRAT'));

  const displayActivityInfo =
    (getMiddleBoolean('ADHERENT_SIT_PRO_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_SIT_PRO_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_CSP_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_CSP_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_PROFESSION_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_PROFESSION_EST_DEMANDE_DONNEES_CONTRAT') &&
      getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_CAISSE_PRO_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_RANG_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_RANG_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_ACTIVITE_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_ACTIVITE_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_REVENU_ANNUEL_EST_DEMANDE_DONNEES_CONTRAT')) ||
    (getMiddleBoolean('ADHERENT_ID_TNS_EST_NECESSAIRE') &&
      getMiddleBoolean('ADHERENT_ID_TNS_EST_DEMANDE_DONNEES_CONTRAT')) ||
    getMiddleBoolean('ADHERENT_CSS_EST_NECESSAIRE');

  return {
    onSubmit,
    unauthorizedModalMessage,
    setUnauthorizedModalMessage,
    isUnauthorized,
    isFetchingForm,
    postalCodeFranceMetro,
    postalCodeDROM,
    $onPrevClick,
    isValid,
    displayAdditionalInfo,
    displayActivityInfo,
    beclmBlocked,
    beclmModalTextKey,
    closeBeclmModal: () => setBeclmModalTextKey(null),
    noEligibleOffer
  };
};

export default useSimulationCustomerGranteesForm;
